import { useState, useContext, useEffect } from "react";
import { RegularButton, } from "../../../common/ReusableComponent/CustomButton";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import ReusableModal from "../../AppPAges/ManageSettings/ReusableModal";
import { ReusableInput, SelectInput } from "../../../common/ReusableComponent/ReusableInput";
import PaginatedSearchSelect from "../../../common/ReusableComponent/PaginatedSearchSelect/PaginatedSearchSelect";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import AddVendorModal from "../../AppPAges/ReusableForm/AddVendorModal";
import { useToastHandler } from "../../../common/ToastHandler";
import AssetTable from "../AssetsInventory/AssetTable";
import { capitalizeFirstLetter } from "../../../../utils/helper/common";
import useSwal from "../../../common/ReusableComponent/useSwal";
import StatusPill from "../../../common/ReusableComponent/StatusPill";
import ActionButton from "../../../common/ReusableComponent/ActionButton";
import { FaCheck } from "react-icons/fa";
import useMyPermissions from "../../../../utils/permissions/useMyPermissions";

const AssetMaintenance = () => {
    const { user } = useContext(AuthContext);
    const { can } = useMyPermissions();
    const { GetPaginatedData, Post, Patch, Delete } = useApi();
    const { handleToast } = useToastHandler();
    const { confirmDelete } = useSwal();
    const [showModal, setShowModal] = useState(false);
    const [showVendorModal, setShowVendorModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [maintenanceData, setMaintenanceData] = useState([]);
    const [refreshTable, setRefreshTable] = useState(0);

    const [selectedAssetName, setSelectedAssetName] = useState("");
    const [selectedUserName, setSelectedUserName] = useState("");
    const [selectedVendorName, setSelectedVendorName] = useState("");
    const [isEdit, setIsEdit] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [showCostModal, setShowCostModal] = useState(false);
    const [isCostEdit, setIsCostEdit] = useState(false);
    const [editingCostId, setEditingCostId] = useState(null);
    const [selectedMaintenanceLabel, setSelectedMaintenanceLabel] = useState("");
    const [selectedVendorId, setSelectedVendorId] = useState("");
    const [showCloseModal, setShowCloseModal] = useState(false);
    const [closingMaintenance, setClosingMaintenance] = useState(null);
    const [closeAction, setCloseAction] = useState("keep");
    const [newAllocatedTo, setNewAllocatedTo] = useState("");
    const [newAllocatedToName, setNewAllocatedToName] = useState("");
    const [formData, setFormData] = useState({
        asset: "",
        maintenance_type: "",
        issue_description: "",
        remarks: "",
        assigned_to: "",
        assigned_to_name: "",
        vendor: "",
        vendor_name: "",
        reported_at: "",
    });

    const [errors, setErrors] = useState({
        asset: "",
        maintenance_type: "",
        assigned_to: "",
        issue_description: "",
        remarks: "",
    });


    const [costFormData, setCostFormData] = useState({
        maintenance_id: "",
        cost_name: "",
        description: "",
        total_cost: "",
        vendor_id: "",
        is_paid: false,
        paid_at: "",
    });

    const handleChange = (e) => {
        const { name, value } = e.target;

        let processedValue = value;
        if (name === "issue_description" || name === "remarks") {
            processedValue = capitalizeFirstLetter(value);
        }

        setFormData({
            ...formData,
            [name]: processedValue,
        });

        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: "",
            });
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        let error = "";

        if (name === "issue_description") {
            if (!value.trim()) {
                error = "Issue description is required";
            } else if (value.length > 500) {
                error = "Issue description must not exceed 500 characters";
            }
        }

        if (name === "remarks" && value && value.length > 300) {
            error = "Remarks must not exceed 300 characters";
        }

        setErrors({
            ...errors,
            [name]: error,
        });
    };

    const fetchMaintenance = async ({ page = 1, searchTerm = "" } = {}) => {
        try {
            setLoading(true);

            const response = await GetPaginatedData("assetMaintenance", {
                page,
                search: searchTerm || undefined,
            });

            const results = response?.data || [];
            setMaintenanceData(results);
            setTotalCount(response?.totalCount || 0);

            return {
                data: results,
                totalPages: Math.ceil(totalCount / 10),
                totalCount,
                currentPage: page,
            };
        } catch {
            handleToast({
                type: "ERROR",
                message: "Failed to fetch maintenance records",
            });

            return {
                data: [],
                totalPages: 1,
                totalCount: 0,
                currentPage: page,
            };
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        let hasErrors = false;
        const newErrors = { ...errors };

        if (!formData.asset) {
            newErrors.asset = "Asset is required";
            hasErrors = true;
        }
        if (!formData.maintenance_type) {
            newErrors.maintenance_type = "Maintenance type is required";
            hasErrors = true;
        }
        if (!formData.assigned_to) {
            newErrors.assigned_to = "Assigned to is required";
            hasErrors = true;
        }
        if (!formData.issue_description.trim()) {
            newErrors.issue_description = "Issue description is required";
            hasErrors = true;
        }

        setErrors(newErrors);
        return !hasErrors;
    };


    const handleAddMaintenance = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        const payload = {
            asset: formData.asset,
            maintenance_type: formData.maintenance_type,
            issue_description: formData.issue_description,
            remarks: formData.remarks,
            assigned_to: Number(formData.assigned_to),
            assigned_to_name: formData.assigned_to_name,
            vendor: formData.vendor || null,
            vendor_name: formData.vendor_name || null,
            reported_at: formData.reported_at || new Date().toISOString(),
        };

        try {
            setLoading(true);

            if (isEdit) {
                await Patch("assetMaintenance", editingId, payload);
                handleToast({ type: "SUCCESS", message: "Maintenance updated successfully" });
            } else {
                await Post("assetMaintenance", {
                    ...payload,
                    store: user.store,
                    reported_by: user.user_id,
                    reported_by_name: user.user_name || null,
                });
                handleToast({ type: "SUCCESS", message: "Maintenance added successfully" });
            }

            // reset
            setShowModal(false);
            setIsEdit(false);
            setEditingId(null);
            setRefreshTable((prev) => prev + 1);

            setFormData({
                asset: "",
                maintenance_type: "",
                issue_description: "",
                remarks: "",
                assigned_to: "",
                assigned_to_name: "",
                vendor: "",
                vendor_name: "",
                reported_at: "",
            });

            setSelectedAssetName("");
            setSelectedUserName("");
            setSelectedVendorName("");
        } catch {
            handleToast({
                type: "ERROR",
                message: isEdit ? "Failed to update record" : "Failed to add record",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEditMaintenance = (rowIdOrObject) => {
        let row;
        if (typeof rowIdOrObject === 'string') {
            row = maintenanceData.find(item => item.id === rowIdOrObject);
            if (!row) {
                console.log("Row not found for ID:", rowIdOrObject);
                return;
            }
        } else {
            row = rowIdOrObject;
        }

        setIsEdit(true);
        setEditingId(row.id);

        setFormData({
            asset: row.asset,
            maintenance_type: row.maintenance_type,
            issue_description: row.issue_description,
            remarks: row.remarks,
            assigned_to: row.assigned_to,
            assigned_to_name: row.assigned_to_name,
            vendor: row.vendor,
            vendor_name: row.vendor_name,
            reported_at: row.reported_at?.split("T")[0] || "",
        });

        setSelectedAssetName(row.asset_name || "");
        setSelectedUserName(row.assigned_to_name || "");
        setSelectedVendorName(row.vendor_name || "");

        setShowModal(true);
    };

    const handleDeleteMaintenance = async (maintenanceIdOrObject) => {
        const id =
            typeof maintenanceIdOrObject === "string"
                ? maintenanceIdOrObject
                : maintenanceIdOrObject?.id;

        if (!id) {
            handleToast({
                type: "ERROR",
                message: "Cannot delete: Invalid maintenance record selected",
            });
            return;
        }

        const result = await confirmDelete(
            "Are you sure?",
            "You won't be able to revert this!"
        );

        if (!result.isConfirmed) return;

        try {
            setLoading(true);

            await Delete("assetMaintenance", id);

            handleToast({
                type: "SUCCESS",
                message: "Maintenance record deleted successfully",
            });

            setRefreshTable((prev) => prev + 1);
        } catch {
            handleToast({
                type: "ERROR",
                message: "Failed to delete maintenance record",
            });
        } finally {
            setLoading(false);
        }
    };
    const handleApproveMaintenance = async (row) => {
        try {
            setLoading(true);

            await Patch("assetMaintenance", row.id, {
                status: "approved",
                approved_by: user.user_id,
                approved_by_name: user.user_name || null,
            });

            handleToast({
                type: "SUCCESS",
                message: "Maintenance approved successfully",
            });

            setRefreshTable(prev => prev + 1);
        } catch {
            handleToast({
                type: "ERROR",
                message: "Failed to approve maintenance",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (row, newStatus) => {
        if (newStatus === "closed") {
            if (!row.is_cost_added) {
                handleToast({
                    type: "ERROR",
                    message: "Please add maintenance cost before closing. If no cost, add cost with 0 amount.",
                });
                return;
            }

            setClosingMaintenance(row);
            setCloseAction("close_only");
            setNewAllocatedTo("");
            setNewAllocatedToName("");
            setShowCloseModal(true);
            return;
        }

        try {
            setLoading(true);

            await Patch("assetMaintenance", row.id, {
                status: newStatus,
            });

            handleToast({
                type: "SUCCESS",
                message: "Status updated successfully",
            });

            setRefreshTable(prev => prev + 1);
        } catch {
            handleToast({
                type: "ERROR",
                message: "Failed to update status",
            });
        } finally {
            setLoading(false);
        }
    };


    const handleConfirmClose = async () => {
        if (!closingMaintenance?.is_cost_added) {
            handleToast({
                type: "ERROR",
                message: "Maintenance cost is required before closing. Add cost with 0 amount if applicable.",
            });
            return;
        }

        try {
            setLoading(true);

            const payload = {
                status: "closed",
                close_action: closeAction,
                allocated_by: user.user_id,
                allocated_by_name: user.user_name || null,
            };

            if (closeAction === "assign_new") {
                payload.assigned_to = Number(newAllocatedTo);
                payload.assigned_to_name = newAllocatedToName;
                payload.allocated_by = user.user_id;
                payload.allocated_by_name = user.user_name || null;
            }

            await Patch("assetMaintenance", closingMaintenance.id, payload);

            handleToast({
                type: "SUCCESS",
                message: "Maintenance closed successfully",
            });

            setShowCloseModal(false);
            setRefreshTable(prev => prev + 1);
        } catch {
            handleToast({
                type: "ERROR",
                message: "Failed to close maintenance",
            });
        } finally {
            setLoading(false);
        }
    };


    const resetMaintenanceCostForm = () => {
        setCostFormData({
            maintenance_id: "",
            cost_name: "",
            description: "",
            total_cost: "",
            vendor_id: "",
            is_paid: false,
            paid_at: "",
        });
        setIsCostEdit(false);
        setEditingCostId(null);
    };

    const handleAddMaintenanceCost = async (e) => {
        e.preventDefault();

        const payload = {
            maintenance_id: costFormData.maintenance_id,
            store: user.store,
            cost_name: costFormData.cost_name,
            description: costFormData.description,
            total_cost: Number(costFormData.total_cost),
            vendor_id: costFormData.vendor_id,
            is_paid: costFormData.is_paid,
            paid_at: costFormData.is_paid
                ? costFormData.paid_at || new Date().toISOString()
                : null,
        };

        try {
            setLoading(true);

            if (isCostEdit) {
                await Patch("assetMaintenanceCost", editingCostId, payload);
                handleToast({ type: "SUCCESS", message: "Maintenance cost updated successfully" });
            } else {
                await Post("assetMaintenanceCost", payload);
                handleToast({ type: "SUCCESS", message: "Maintenance cost added successfully" });
            }

            setShowCostModal(false);
            resetMaintenanceCostForm();
            setRefreshTable(prev => prev + 1);
        } catch {
            handleToast({
                type: "ERROR",
                message: isCostEdit ? "Failed to update cost" : "Failed to add cost",
            });
        } finally {
            setLoading(false);
        }
    };

    const MAINTENANCE_STATUS_PILL = {
        reported: {
            bg: "#fff3cd",
            text: "#856404",
            border: "#ffeeba",
        },
        approved: {
            bg: "#ecfeff",
            text: "#0891b2",
            border: "#bae6fd",
        },
        in_progress: {
            bg: "#fff7ed",
            text: "#ea580c",
            border: "#fed7aa",
        },
        closed: {
            bg: "#e9fdf3",
            text: "#12b76a",
            border: "#a6f4c5",
        },
        cancelled: {
            bg: "#f1f3f5",
            text: "#6c757d",
            border: "#ced4da",
        },
    };

    const columns = [
        {
            header: "Asset",
            field: "asset_name",
        },
        {
            header: "Type",
            field: "maintenance_type",
            render: (row) =>
                row?.maintenance_type
                    ? row.maintenance_type.charAt(0).toUpperCase() +
                    row.maintenance_type.slice(1)
                    : "-",
        },

        {
            header: "Description",
            field: "issue_description",
            render: (row) => row?.issue_description || "-",
        },
        {
            header: "Remarks",
            field: "remarks",
            render: (row) => row?.remarks || "-",
        },
        {
            header: "Report From",
            field: "reported_by_name",

        },
        {
            header: "Assigned To",
            field: "assigned_to_name",
        },
        {
            header: "Vendor",
            field: "vendor_name",
        },
        {
            header: "Reported At",
            field: "reported_at",
            render: (row) =>
                row?.reported_at
                    ? new Date(row.reported_at).toLocaleDateString()
                    : "-",
        },
        {
            header: "Status",
            render: (row) => {
                const cfg =
                    MAINTENANCE_STATUS_PILL[row.status] ||
                    MAINTENANCE_STATUS_PILL.reported;

                // only approved / in_progress can change
                if (row.status === "approved" || row.status === "in_progress") {
                    return (
                        <select
                            value={row.status}
                            onChange={(e) => handleStatusChange(row, e.target.value)}
                            style={{
                                padding: "6px 8px",
                                borderRadius: "6px",
                                border: "1px solid #ddd",
                                fontSize: "14px",
                            }}
                        >
                            <option value="approved">Approved</option>
                            <option value="in_progress">In Progress</option>
                            <option value="closed">Closed</option>
                        </select>
                    );
                }

                return (
                    <StatusPill
                        label={row.status}
                        bgColor={cfg.bg}
                        textColor={cfg.text}
                        borderColor={cfg.border}
                    />
                );
            },
        },
        {
            header: "Request",
            render: (row) => {
                if (row.status === "reported") {
                    return (
                        <ActionButton
                            onClick={() => handleApproveMaintenance(row)}
                            bgColor="#e9fdf3"
                            textColor="#12b76a"
                            borderColor="#a6f4c5"
                            hoverBgColor="#d1fae5"
                        >
                            <FaCheck />
                            Approve
                        </ActionButton>
                    );
                }

                return "-";
            },
        }


    ];

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchMaintenance({ page: currentPage, searchTerm: debouncedSearchTerm });
    }, [currentPage, debouncedSearchTerm, refreshTable]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    };

    const resetForm = () => {
        setFormData({
            asset: "",
            maintenance_type: "",
            issue_description: "",
            remarks: "",
            assigned_to: "",
            assigned_to_name: "",
            vendor: "",
            vendor_name: "",
            reported_at: "",
        });
        setSelectedAssetName("");
        setSelectedUserName("");
        setSelectedVendorName("");
        setErrors({
            asset: "",
            maintenance_type: "",
            assigned_to: "",
            issue_description: "",
            remarks: "",
        });
        setIsEdit(false);
        setEditingId(null);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        resetForm();
    };

    return (
        <div className="responsive-box">
            <div className="inventory-header">
                <div className="inventory-title">
                    Assets Maintenance
                </div>
                <div
                    className="btn-position"
                    style={{ display: "flex", gap: "10px" }}
                >
                </div>
            </div>

            <AssetTable
                data={maintenanceData}
                columns={columns}
                loading={loading}
                showAction={true}
                onEdit={can("assetService", "change") ? handleEditMaintenance : undefined}
                onDelete={can("assetService", "delete") ? handleDeleteMaintenance : undefined}
                showFooter
                currentPage={currentPage}
                totalCount={totalCount}
                onPageChange={handlePageChange}
                searchTerm={searchTerm}
                onSearch={handleSearch}
            />

            <ReusableModal
                title={isEdit ? "Edit Maintenance" : "Add Maintenance"}
                show={showModal}
                handleClose={handleCloseModal}
                width="550px"
            >
                <form onSubmit={handleAddMaintenance}>
                    <div
                        className="d-flex flex-column"
                        style={{
                            marginLeft: "0px",
                            marginRight: "0px",
                        }}
                    >
                        <div className="d-flex align-items-center mx-3">
                            <div className="d-flex flex-column" style={{ width: "250px" }}>
                                <PaginatedSearchSelect
                                    api="asset"
                                    GetPaginatedData={GetPaginatedData}
                                    getOptionLabel={(opt) => opt?.name || ""}
                                    onSelect={(opt) => {
                                        setFormData((prev) => ({
                                            ...prev,
                                            asset: opt?.id || "",
                                            vendor: opt?.vendor || "",
                                            vendor_name: opt?.vendor_name || "",
                                        }));
                                        setSelectedAssetName(opt?.name || "");
                                        setSelectedVendorName(
                                            opt?.vendor_name || ""
                                        );
                                        if (errors.asset) {
                                            setErrors((prev) => ({ ...prev, asset: "" }));
                                        }
                                    }}
                                    value={
                                        formData.asset && selectedAssetName
                                            ? {
                                                id: formData.asset,
                                                name: selectedAssetName,
                                            }
                                            : null
                                    }
                                    placeholder="Select Asset *"
                                    required
                                    style={{ width: "250px" }}
                                    error={errors.asset}
                                />
                                {errors.asset && (
                                    <div className="text-danger" style={{ fontSize: "12px", marginTop: "4px" }}>
                                        {errors.asset}
                                    </div>
                                )}
                            </div>
                            <div className="d-flex align-items-center mt-4">
                                <div className="d-flex flex-column" style={{ width: "250px" }}>
                                    <SelectInput
                                        value={formData.maintenance_type}
                                        onChange={(e) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                maintenance_type: e.target.value,
                                            }));
                                            if (errors.maintenance_type) {
                                                setErrors((prev) => ({ ...prev, maintenance_type: "" }));
                                            }
                                        }}
                                        options={[
                                            { id: "repair", name: "Repair" },
                                            { id: "service", name: "Service" },
                                            { id: "inspection", name: "Inspection" },
                                        ]}
                                        label="Maintenance Type"
                                        placeholder="Select Maintenance Type"
                                        required
                                        style={{ width: "250px" }}
                                        error={errors.maintenance_type}
                                    />
                                    {errors.maintenance_type && (
                                        <div className="text-danger" style={{ fontSize: "12px", marginTop: "4px" }}>
                                            {errors.maintenance_type}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="d-flex align-items-start mb-3 mx-3">
                            <div className="d-flex flex-column" style={{ width: "250px" }}>
                                <PaginatedSearchSelect
                                    api="user"
                                    GetPaginatedData={GetPaginatedData}
                                    getOptionLabel={(opt) =>
                                        opt?.name ||
                                        opt?.first_name ||
                                        opt?.username
                                    }
                                    onSelect={(opt) => {
                                        setFormData((prev) => ({
                                            ...prev,
                                            assigned_to: opt?.id || "",
                                            assigned_to_name:
                                                opt?.name ||
                                                opt?.first_name ||
                                                opt?.username ||
                                                "",
                                        }));
                                        setSelectedUserName(
                                            opt?.name ||
                                            opt?.first_name ||
                                            opt?.username ||
                                            ""
                                        );
                                        if (errors.assigned_to) {
                                            setErrors((prev) => ({ ...prev, assigned_to: "" }));
                                        }
                                    }}
                                    value={
                                        formData.assigned_to && selectedUserName
                                            ? {
                                                id: formData.assigned_to,
                                                name: selectedUserName,
                                            }
                                            : null
                                    }
                                    placeholder="Assign To *"
                                    required
                                    style={{ width: "250px" }}
                                    error={errors.assigned_to}
                                />
                                {errors.assigned_to && (
                                    <div className="text-danger" style={{ fontSize: "12px", marginTop: "4px" }}>
                                        {errors.assigned_to}
                                    </div>
                                )}
                            </div>
                            <PaginatedSearchSelect
                                api="vendorDepartmentData"
                                GetPaginatedData={GetPaginatedData}
                                getOptionLabel={(opt) => opt.name}
                                onSelect={(opt) => {
                                    if (opt?.id === "add-vendor") {
                                        setShowVendorModal(true);
                                    } else {
                                        setFormData((prev) => ({
                                            ...prev,
                                            vendor: opt?.id || "",
                                            vendor_name: opt?.name || "",
                                        }));
                                        setSelectedVendorName(
                                            opt?.name || ""
                                        );
                                    }
                                }}
                                value={
                                    formData.vendor && selectedVendorName
                                        ? {
                                            id: formData.vendor,
                                            name: selectedVendorName,
                                        }
                                        : null
                                }
                                placeholder="Select Vendor"
                                extraOptions={[
                                    {
                                        id: "add-vendor",
                                        label: "Add Vendor",
                                    },
                                ]}
                                style={{ width: "250px" }}
                            />
                        </div>
                        <div className="d-flex align-items-center mb-3 mx-3">
                            <ReusableInput
                                type="text"
                                name="issue_description"
                                label="Issue Description"
                                value={formData.issue_description}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                maxLength={500}
                                error={errors.issue_description}
                                width={"600px"}
                                required
                            />
                            <ReusableInput
                                type="text"
                                label="Remarks"
                                name="remarks"
                                value={formData.remarks}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                maxLength={300}
                                error={errors.remarks}
                            />
                        </div>
                        <div className="d-flex align-items-center mb-3 mx-3">
                            <div>
                                <ReusableInput
                                    type="date"
                                    className="mt-2"
                                    value={formData.reported_at}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            reported_at: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <div></div>
                        </div>
                        <div className="d-flex justify-content-end mt-3">
                            {!isEdit && (
                                <RegularButton
                                    text="Clear"
                                    onClick={resetForm}
                                    backgroundColor="#ffffff"
                                    color="#282E26"
                                />
                            )}
                            <RegularButton
                                type="submit"
                                text={
                                    loading ? (isEdit ? "Updating..." : "Saving...") : (isEdit ? "Update" : "Save")
                                }
                                backgroundColor="#282e26"
                                disabled={loading}
                            />
                        </div>
                    </div>
                </form>
            </ReusableModal>

            <ReusableModal
                title={isCostEdit ? "Edit Maintenance Cost" : "Add Maintenance Cost"}
                show={showCostModal}
                handleClose={() => {
                    setShowCostModal(false);
                    resetMaintenanceCostForm();
                }}
                width="500px"
            >
                <form onSubmit={handleAddMaintenanceCost}>
                    <PaginatedSearchSelect
                        api="assetMaintenance"
                        GetPaginatedData={GetPaginatedData}
                        style={{ width: "500px" }}
                        getOptionLabel={(opt) => opt?.asset_name || ""}
                        onSelect={(opt) => {
                            setCostFormData(prev => ({
                                ...prev,
                                maintenance_id: opt?.id || "",
                            }));
                            setSelectedMaintenanceLabel(opt?.asset_name || "");
                        }}
                        placeholder="Select Maintenance"
                        pageSize={10}
                        filterFn={(item) =>
                            item.status === "approved" && !item.deleted_at
                        }
                        value={
                            costFormData.maintenance_id
                                ? {
                                    id: costFormData.maintenance_id,
                                    asset_name: selectedMaintenanceLabel,
                                }
                                : null
                        }
                    />
                    <ReusableInput
                        label="Cost Name"
                        value={costFormData.cost_name}
                        onChange={(e) =>
                            setCostFormData(prev => ({ ...prev, cost_name: e.target.value }))
                        }
                        required
                    />

                    <ReusableInput
                        label="Description"
                        value={costFormData.description}
                        onChange={(e) =>
                            setCostFormData(prev => ({ ...prev, description: e.target.value }))
                        }
                    />

                    <ReusableInput
                        type="number"
                        label="Total Cost"
                        value={costFormData.total_cost}
                        onChange={(e) =>
                            setCostFormData(prev => ({ ...prev, total_cost: e.target.value }))
                        }
                        required
                    />

                    <PaginatedSearchSelect
                        api="vendorDepartmentData"
                        GetPaginatedData={GetPaginatedData}
                        style={{ width: "500px" }}
                        getOptionLabel={(opt) => opt?.name || ""}
                        onSelect={(opt) => {
                            if (opt?.id === "add-vendor") {
                                setShowVendorModal(true);
                            } else {
                                setSelectedVendorId(opt?.id || "");
                                setSelectedVendorName(opt?.name || "");
                                setCostFormData(prev => ({
                                    ...prev,
                                    vendor_id: opt?.id || "",
                                }));
                            }
                        }}
                        placeholder="Select Vendor"
                        pageSize={10}
                        value={
                            selectedVendorId
                                ? { id: selectedVendorId, name: selectedVendorName }
                                : null
                        }
                        filterFn={(vendor) =>
                            vendor.type === "purchase" && !vendor.is_deleted
                        }
                        extraOptions={[{ id: "add-vendor", label: "Add Vendor" }]}
                    />


                    <div className="d-flex align-items-center gap-2 mt-2">
                        <input
                            type="checkbox"
                            checked={costFormData.is_paid}
                            onChange={(e) =>
                                setCostFormData(prev => ({ ...prev, is_paid: e.target.checked }))
                            }
                        />
                        <label>Paid</label>
                    </div>

                    {costFormData.is_paid && (
                        <ReusableInput
                            type="date"
                            label="Paid At"
                            value={costFormData.paid_at}
                            onChange={(e) =>
                                setCostFormData(prev => ({ ...prev, paid_at: e.target.value }))
                            }
                        />
                    )}

                    <div className="d-flex justify-content-end mt-3">
                        <RegularButton
                            type="submit"
                            text={isCostEdit ? "Update" : "Save"}
                            backgroundColor="#282e26"
                        />
                    </div>
                </form>
            </ReusableModal>

            {showVendorModal && (
                <AddVendorModal
                    show={showVendorModal}
                    handleClose={() =>
                        setShowVendorModal(false)
                    }
                />
            )}

            <ReusableModal
                title="Close Maintenance"
                show={showCloseModal}
                handleClose={() => setShowCloseModal(false)}
                width="500px"
            >
                <div className="p-3">
                    <p>Are you sure you want to close this maintenance?</p>

                    <div className="mb-3">
                        <label className="d-flex align-items-center gap-2">
                            <input
                                type="radio"
                                name="closeAction"
                                value="close_only"
                                checked={closeAction === "close_only"}
                                onChange={(e) => setCloseAction(e.target.value)}
                            />
                            Close only
                        </label>
                    </div>

                    <div className="mb-3">
                        <label className="d-flex align-items-center gap-2">
                            <input
                                type="radio"
                                name="auto_assign"
                                value="auto_assign"
                                checked={closeAction === "auto_assign"}
                                onChange={(e) => setCloseAction(e.target.value)}
                            />
                            Close with existing employee
                        </label>
                    </div>

                    <div className="mb-3">
                        <label className="d-flex align-items-center gap-2">
                            <input
                                type="radio"
                                name="closeAction"
                                value="assign_new"
                                checked={closeAction === "assign_new"}
                                onChange={(e) => setCloseAction(e.target.value)}
                            />
                            Close and reallocate to another employee
                        </label>
                    </div>

                    {closeAction === "assign_new" && (
                        <div className="mb-3">
                            <PaginatedSearchSelect
                                api="user"
                                GetPaginatedData={GetPaginatedData}
                                getOptionLabel={(opt) =>
                                    opt?.name || opt?.first_name || opt?.username
                                }
                                onSelect={(opt) => {
                                    setNewAllocatedTo(opt?.id || "");
                                    setNewAllocatedToName(
                                        opt?.name || opt?.first_name || opt?.username || ""
                                    );
                                }}
                                value={
                                    newAllocatedTo
                                        ? { id: newAllocatedTo, name: newAllocatedToName }
                                        : null
                                }
                                placeholder="Select Employee"
                                required
                            />
                        </div>
                    )}

                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <RegularButton
                            text="Cancel"
                            backgroundColor="#6c757d"
                            onClick={() => setShowCloseModal(false)}
                        />
                        <RegularButton
                            text="Confirm Close"
                            backgroundColor="#198F51"
                            onClick={handleConfirmClose}
                            disabled={closeAction === "reallocate" && !newAllocatedTo}
                        />
                    </div>
                </div>
            </ReusableModal>
        </div>
    );
};

export default AssetMaintenance;
